import { lazy } from "react";

import "./scenes.css";
import { SCENE_IDS, type SceneId } from "./ids";

import type { ComponentType, LazyExoticComponent } from "react";

// The public website's animated concept scenes, vendored verbatim under
// ./vendor by scripts/sync-scenes.mjs (see ./vendor/VENDOR.md for the site
// commit), which also generates this file. Each scene is a self-contained
// SVG/CSS-keyframe component that takes an optional className; they lean on
// Tailwind utility classes, which ./scenes.css provides (theme + utilities
// only, no preflight, so MUI is untouched).
//
// Every scene is code-split: a row's chunk loads when the row renders, so
// registering all of them costs the main bundle nothing.
export type SceneComponent = ComponentType<{ className?: string }>;

export const SCENES: Record<SceneId, LazyExoticComponent<SceneComponent>> = {
    AccountConsolidation: lazy(() => import("./vendor/AccountConsolidation")),
    AccountOwnerStage: lazy(() => import("./vendor/AccountOwnerStage")),
    AccountProfitCompare: lazy(() => import("./vendor/AccountProfitCompare")),
    AiAutomation: lazy(() => import("./vendor/AiAutomation")),
    AiCatalogStage: lazy(() => import("./vendor/AiCatalogStage")),
    AplusCreation: lazy(() => import("./vendor/AplusCreation")),
    BidBudgetOptimization: lazy(() => import("./vendor/BidBudgetOptimization")),
    BrandBuildStage: lazy(() => import("./vendor/BrandBuildStage")),
    BrandCarousel: lazy(() => import("./vendor/BrandCarousel")),
    BrandElevation: lazy(() => import("./vendor/BrandElevation")),
    BrandPlaybook: lazy(() => import("./vendor/BrandPlaybook")),
    BuyBoxRace: lazy(() => import("./vendor/BuyBoxRace")),
    BuyBoxStage: lazy(() => import("./vendor/BuyBoxStage")),
    BuyBoxWin: lazy(() => import("./vendor/BuyBoxWin")),
    CampaignPortfolio: lazy(() => import("./vendor/CampaignPortfolio")),
    CarrierBookingStage: lazy(() => import("./vendor/CarrierBookingStage")),
    CarrierIntegration: lazy(() => import("./vendor/CarrierIntegration")),
    CarrierRateCompare: lazy(() => import("./vendor/CarrierRateCompare")),
    ChannelAccessStage: lazy(() => import("./vendor/ChannelAccessStage")),
    ChannelAdsStage: lazy(() => import("./vendor/ChannelAdsStage")),
    ChannelPricingStage: lazy(() => import("./vendor/ChannelPricingStage")),
    CompetitorPricing: lazy(() => import("./vendor/CompetitorPricing")),
    CompetitorPricingClassic: lazy(() => import("./classic/CompetitorPricing")),
    ConnectionStage: lazy(() => import("./vendor/ConnectionStage")),
    CustomIntegrations: lazy(() => import("./vendor/CustomIntegrations")),
    Dayparting: lazy(() => import("./vendor/Dayparting")),
    DeliveryTrackStage: lazy(() => import("./vendor/DeliveryTrackStage")),
    DemandChart: lazy(() => import("./vendor/DemandChart")),
    EnforcementWorkflow: lazy(() => import("./vendor/EnforcementWorkflow")),
    ExperimentImpact: lazy(() => import("./vendor/ExperimentImpact")),
    FastOnboarding: lazy(() => import("./vendor/FastOnboarding")),
    FastReplacement: lazy(() => import("./vendor/FastReplacement")),
    ImageEnrichment: lazy(() => import("./vendor/ImageEnrichment")),
    InfographicCreation: lazy(() => import("./vendor/InfographicCreation")),
    InventoryFlow: lazy(() => import("./vendor/InventoryFlow")),
    IssueResolution: lazy(() => import("./vendor/IssueResolution")),
    KeywordEditor: lazy(() => import("./vendor/KeywordEditor")),
    LaunchRamp: lazy(() => import("./vendor/LaunchRamp")),
    LaunchRampClassic: lazy(() => import("./classic/LaunchRamp")),
    ListingCreation: lazy(() => import("./vendor/ListingCreation")),
    ListingHealthscore: lazy(() => import("./vendor/ListingHealthscore")),
    ListingHealthscoreClassic: lazy(() => import("./classic/ListingHealthscore")),
    ListingManagement: lazy(() => import("./vendor/ListingManagement")),
    ListingManagementClassic: lazy(() => import("./classic/ListingManagement")),
    MapGuard: lazy(() => import("./vendor/MapGuard")),
    MapWatch: lazy(() => import("./vendor/MapWatch")),
    MapWatchStage: lazy(() => import("./vendor/MapWatchStage")),
    MarketplaceExpansion: lazy(() => import("./vendor/MarketplaceExpansion")),
    MoneyWaterfall: lazy(() => import("./vendor/MoneyWaterfall")),
    MultiMarketplaceAds: lazy(() => import("./vendor/MultiMarketplaceAds")),
    NationwideFulfillment: lazy(() => import("./vendor/NationwideFulfillment")),
    NationwideStage: lazy(() => import("./vendor/NationwideStage")),
    NewDemandMonthly: lazy(() => import("./vendor/NewDemandMonthly")),
    NewDemandStage: lazy(() => import("./vendor/NewDemandStage")),
    OnTimeDelivery: lazy(() => import("./vendor/OnTimeDelivery")),
    OneConnection: lazy(() => import("./vendor/OneConnection")),
    PlaybookRewrite: lazy(() => import("./vendor/PlaybookRewrite")),
    PortalStage: lazy(() => import("./vendor/PortalStage")),
    PricingAutomation: lazy(() => import("./vendor/PricingAutomation")),
    PricingEngineGrid: lazy(() => import("./vendor/PricingEngineGrid")),
    PricingTicker: lazy(() => import("./vendor/PricingTicker")),
    PricingUniverse: lazy(() => import("./vendor/PricingUniverse")),
    PricingUniverseClassic: lazy(() => import("./classic/PricingUniverse")),
    ProfitOptimization: lazy(() => import("./vendor/ProfitOptimization")),
    ProfitOptimizationClassic: lazy(() => import("./classic/ProfitOptimization")),
    ProfitStackStage: lazy(() => import("./vendor/ProfitStackStage")),
    PromoSync: lazy(() => import("./vendor/PromoSync")),
    PromoSyncClassic: lazy(() => import("./classic/PromoSync")),
    RegionalPriceStage: lazy(() => import("./vendor/RegionalPriceStage")),
    RegionalPricing: lazy(() => import("./vendor/RegionalPricing")),
    RegionalShipSpeeds: lazy(() => import("./vendor/RegionalShipSpeeds")),
    ReplacementStage: lazy(() => import("./vendor/ReplacementStage")),
    ReturningCustomersStage: lazy(() => import("./vendor/ReturningCustomersStage")),
    ReturnsNetwork: lazy(() => import("./vendor/ReturnsNetwork")),
    ReturnsQueueStage: lazy(() => import("./vendor/ReturnsQueueStage")),
    ReviewAnalysis: lazy(() => import("./vendor/ReviewAnalysis")),
    ReviewMonitoring: lazy(() => import("./vendor/ReviewMonitoring")),
    SearchRanking: lazy(() => import("./vendor/SearchRanking")),
    SecondLifeStage: lazy(() => import("./vendor/SecondLifeStage")),
    SetYourPrice: lazy(() => import("./vendor/SetYourPrice")),
    SetYourPriceStage: lazy(() => import("./vendor/SetYourPriceStage")),
    ShipSpeedStage: lazy(() => import("./vendor/ShipSpeedStage")),
    StorefrontStage: lazy(() => import("./vendor/StorefrontStage")),
    SupplierPortal: lazy(() => import("./vendor/SupplierPortal")),
    SupportLoopStage: lazy(() => import("./vendor/SupportLoopStage")),
    TailoredStorefront: lazy(() => import("./vendor/TailoredStorefront")),
    TechPlatformStage: lazy(() => import("./vendor/TechPlatformStage")),
    UnauthorizedSellers: lazy(() => import("./vendor/UnauthorizedSellers")),
    UseOurTechnology: lazy(() => import("./vendor/UseOurTechnology")),
};

export { SCENE_IDS };
export type { SceneId };
